<template>
    <div class="note-applied-goal-modal">
        <GoalModal
            :is-open="isOpen"
            :title="$t('funnel.edit.sideBar.goal.apply.note.title')"
            :summary-text="$t('funnel.edit.sideBar.goal.apply.note.summary')"
            :goal-tip-text="$t('funnel.edit.sideBar.goal.apply.note.tip.overview')"
            :goal-tip-link="toolTipLink"
            :cell="cell"
            :cell-type="cellType"
            @update-config="saveCellValue"
            @close-modal="closeModal"
        >
            <template #goalParameters>
                <div class="vertical-container">
                    <MergeableInput
                        v-model="note.actionDescription"
                        data-qa="note-action-description-input-value"
                        required
                        :error-text="$t('funnel.edit.sideBar.goal.apply.note.required')"
                        :invalid="!isValidDescription"
                        :submitted="submitted"
                        :merge-field-categories="mergeFields"
                        :label="$t('funnel.edit.sideBar.goal.apply.note.placeholder.note.title')"
                        class="note-action-description-input-class"
                    />

                    <DsMultiselect
                        v-model="note.actionType"
                        required
                        :force-invalid="emptyActionType"
                        bind-value-only
                        :options="actionTypeOptions"
                        :placeholder="$t('funnel.edit.sideBar.goal.apply.note.placeholder.note.action.type')"
                        class="multi-select-action-type"
                        @input="clearActionTypeSelection"
                    >
                        <template v-if="emptyActionType" #help>
                            {{ $t('funnel.edit.sideBar.goal.apply.note.required') }}
                        </template>
                    </DsMultiselect>

                    <CampaignBuilderMergeableTextArea
                        v-model="note.creationNotes"
                        data-qa="note-creation-notes-input-value"
                        :merge-field-categories="mergeFields"
                        :label="$t('funnel.edit.sideBar.goal.apply.note.placeholder.description')"
                        class="note-creation-notes-input-class"
                    />

                    <DsMultiselect
                        v-model="selectedLoggedInUser"
                        bind-value-only
                        :readonly="loading"
                        :loading="loading"
                        :options="usersOptions"
                        :placeholder="$t('funnel.edit.sideBar.goal.apply.note.placeholder.assigned.to')"
                        class="select-logged-in-user"
                    />

                    <DsMultiselect
                        key="selectedUsers"
                        v-model="selectedNotifiedUsers"
                        multiple
                        searchable
                        :readonly="loading"
                        :loading="loading"
                        bind-value-only
                        :options="notifyUsersOptions"
                        :name="$t('funnel.edit.sideBar.goal.apply.note.placeholder.notify.users')"
                        :placeholder="$t('funnel.edit.sideBar.goal.apply.note.placeholder.notify.users')"
                        class="select-multiple-users"
                    />
                </div>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import { utils } from '../../../utils/legacy-utils';
import { CONSTANTS_EXTERNAL_LINKS, GOAL_NOTE_APPLIED } from '../../../utils/editor.constants';
import {
    createNoteApplied,
    getNoteAppliedConfig,
    updateNoteApplied,
} from './note.applied.api';
import GoalModal from '../goalModal/GoalModal.vue';
import MergeableInput from '@/components/shared/MergeableInput.vue';
import CampaignBuilderMergeableTextArea from '../campaignBuilderMergeableTextArea/CampaignBuilderMergeableTextArea.vue';

export default {
    name: 'NoteApplied',
    components: {
        GoalModal,
        MergeableInput,
        CampaignBuilderMergeableTextArea,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    marketingNoteId: 0,
                },
            }),
        },
        mergeFields: {
            type: Array,
            required: true,
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            cellType: GOAL_NOTE_APPLIED,
            note: {
                marketingNoteId: 0,
                actionDescription: '',
                actionType: null,
                creationNotes: '',
            },
            selectedLoggedInUser: 0,
            usersOptions: [],
            selectedNotifiedUsers: [],
            actionTypeOptions: [],
            notifyUsersOptions: [],
            userIdSystem: null,
            loading: false,
            emptyActionType: false,
            submitted: false,
        };
    },
    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        toolTipLink() {
            return this.isSpaApp ? CONSTANTS_EXTERNAL_LINKS.noteAppliedKeapLink : CONSTANTS_EXTERNAL_LINKS.noteAppliedClassicLink;
        },
        isValidDescription() {
            return !!this.note.actionDescription;
        },
    },
    mounted() {
        this.loadNoteAppliedConfig();
    },
    methods: {
        isEmptyActionType() {
            this.emptyActionType = !this.note.actionType;
        },
        isValid() {
            this.isEmptyActionType();

            return this.isValidDescription && !this.emptyActionType;
        },
        async saveCellValue() {
            try {
                this.submitted = true;

                if (this.isValid()) {
                    const cell = { ...this.cell };

                    cell.value = cell.value || {};
                    cell.value.marketingNoteId = utils.removeLongSuffix(this.note.marketingNoteId);

                    const payload = {
                        actionType: this.note?.actionType,
                        actionDescription: this.note?.actionDescription,
                        creationNotes: this.note?.creationNotes,
                        assignmentUserId: this.selectedLoggedInUser === this.userIdSystem ? 0 : this.selectedLoggedInUser,
                        notifyUsers: this.selectedNotifiedUsers.map((nu) => utils.ensureLongEndsWithL(nu)),
                    };

                    await updateNoteApplied(cell.value.marketingNoteId, payload);

                    this.$emit('update-cell-value', { cell });

                    this.submitted = false;
                }
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.sideBar.goal.apply.note.create.error');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        closeModal() {
            this.$emit('close-modal');
        },
        async loadNoteAppliedConfig() {
            try {
                this.loading = true;
                let marketingNoteIdValue = 0;

                if (this.cell.value?.marketingNoteId) {
                    marketingNoteIdValue = this.cell.value.marketingNoteId;
                } else {
                    const noteLabel = this.cell.value?.name ?? this.$t('funnel.sidebar.when.cell.noteApplied.label');

                    marketingNoteIdValue = await createNoteApplied(noteLabel);
                }

                this.note.marketingNoteId = marketingNoteIdValue;

                const {
                    note,
                    typeMap,
                    userMap,
                    userIdSystem,
                    notifyUsers,
                } = await getNoteAppliedConfig(marketingNoteIdValue);

                this.note.actionDescription = note.actionDescription;
                this.note.creationNotes = note.creationNotes;

                this.userIdSystem = userIdSystem;

                this.actionTypeOptions = Object.entries(typeMap).map(([value, label]) => ({
                    value,
                    label,
                }));
                this.note.actionType = note.actionType;

                this.usersOptions = [
                    {
                        value: this.userIdSystem,
                        label: this.$t('funnel.edit.sideBar.goal.apply.note.assignment.user.option'),
                    },
                    ...Object.entries(userMap).map(([value, label]) => ({
                        value: parseInt(value, 10),
                        label,
                    })),
                ];

                this.selectedLoggedInUser = this.usersOptions.find((o) => o.value === parseInt(note.assignmentUserId, 10))?.value ?? this.userIdSystem;

                this.notifyUsersOptions = Object.entries(userMap).map(([value, label]) => ({
                    value: utils.ensureLongEndsWithL(value),
                    label,
                }));

                this.selectedNotifiedUsers = notifyUsers;
            } catch (error) {
                const errorMessage = `Could not load note applied: ${error}`;

                this.$error({
                    message: errorMessage,
                });
            } finally {
                this.loading = false;
            }
        },
        clearActionTypeSelection() {
            this.emptyActionType = false;
        },
    },
};
</script>

<style lang="scss" scoped>

.vertical-container {
    display: flex;
    flex-direction: column;
}

</style>
